import { useState } from 'react';
import { apiWrite } from '../../lib/api-client.js';

interface WriteResult<T = unknown> {
  ok: boolean;
  error?: string;
  data?: T;
}

export function useFleetWrite(onSuccess: () => void) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function call<T = unknown>(path: string, method: string, body: unknown): Promise<WriteResult<T>> {
    setSubmitting(true);
    setError(null);
    try {
      const { ok, data } = await apiWrite<T & { error?: string }>(path, method, body);
      if (!ok || (data as { error?: string })?.error) {
        const msg = (data as { error?: string })?.error || 'Request failed';
        setError(msg);
        return { ok: false, error: msg };
      }
      onSuccess();
      return { ok: true, data };
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Connection error';
      setError(msg);
      return { ok: false, error: msg };
    } finally {
      setSubmitting(false);
    }
  }

  async function createVehicle(body: {
    truck_number: string;
    lease_type: string;
    current_terminal_id: string | number;
    vin?: string;
    license_plate?: string;
    last_known_mileage?: number;
  }) {
    return call('/api/fleet/vehicles', 'POST', body);
  }

  async function updateVehicle(vehicleId: string | number, body: Record<string, unknown>) {
    return call(`/api/fleet/vehicles/${vehicleId}`, 'PUT', body);
  }

  async function createRental(body: {
    rental_unit_number: string;
    rental_agreement_number: string;
    expiration_date: string;
    vendor?: string;
    vin?: string;
    license_plate?: string;
    terminal_id?: string | number;
  }) {
    return call('/api/fleet/rentals', 'POST', body);
  }

  async function updateRental(rentalId: string | number, body: Record<string, unknown>) {
    return call(`/api/fleet/rentals/${rentalId}`, 'PUT', body);
  }

  async function cancelTransfer(transferId: string | number) {
    return call(`/api/fleet/transfers/${transferId}/cancel`, 'POST', {});
  }

  async function createInspection(body: {
    vehicle_id: string | number;
    overall_rating: string;
    transfer_id?: string | number;
    inspection_terminal_id?: string | number;
    mileage_at_inspection?: number;
    oil_condition?: string;
    transmission_condition?: string;
    tire_condition?: string;
    brake_condition?: string;
    body_damage_notes?: string;
    dot_status?: string;
    insurance_status?: string;
    notes?: string;
  }) {
    return call('/api/fleet/inspections', 'POST', body);
  }

  async function resolveIssue(issueId: string | number, body: {
    resolved_by: string;
    status?: string;
    resolution_notes?: string;
  }) {
    return call(`/api/fleet/issues/${issueId}/resolve`, 'PUT', body);
  }

  return {
    createVehicle,
    updateVehicle,
    createRental,
    updateRental,
    cancelTransfer,
    createInspection,
    resolveIssue,
    submitting,
    error,
    clearError: () => setError(null),
  };
}
